import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  fetchAgent,
  fetchAgents,
  revokeAgent,
  rotateAgent,
} from '../lib/api';
import type {
  Agent,
  AgentDetail,
  ApiError,
  RotateAgentResponse,
} from '../lib/api';
import { copyToClipboard } from '../lib/clipboard';
import { identitySegment, REP_SEGMENT } from '../lib/orgRep';
import { agentIdentity } from '../lib/spaceIdentity';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { ConnectAgentModal } from './ConnectAgentModal';
import { EmptyState } from './EmptyState';
import { Skeleton } from './Skeleton';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function AgentsSection() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['agents'],
    queryFn: fetchAgents,
  });

  const [connectOpen, setConnectOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="settings-section">
        <h3>Agents</h3>
        <Skeleton
          className="settings-section-loading"
          ariaLabel="Loading agents"
          rows={3}
          variant="list"
        />
      </div>
    );
  }

  if (isError) {
    const apiError = error as unknown as ApiError | undefined;
    return (
      <div className="settings-section">
        <h3>Agents</h3>
        <div className="login-form-error" role="alert">
          {apiError?.status === 401
            ? 'Sign in to view your agents.'
            : "We couldn't load your agents. Try refreshing the page."}
        </div>
      </div>
    );
  }

  const agents = data?.agents ?? [];

  return (
    <div className="settings-section">
      <h3>Agents</h3>
      <p className="settings-section-help">
        Agents that act on your behalf. Connect one for yourself, or set one up
        for a coworker — we hand you a one-line command to paste, no tokens to
        copy by hand.
      </p>

      <div className="settings-actions">
        <button
          type="button"
          className="settings-action-primary"
          onClick={() => setConnectOpen(true)}
        >
          Connect an agent
        </button>
      </div>

      <h4 className="settings-subhead">Your agents</h4>
      {agents.length === 0 ? (
        <EmptyState
          className="settings-empty-state agents-empty-state"
          icon="🤖"
          title="No agents yet"
          description="Connect one above or from the CLI to give work a dedicated agent identity."
          action={
            <code className="settings-cli-hint-code">
              tizok agent create &lt;name&gt;
            </code>
          }
        />
      ) : (
        <ul className="agents-list" aria-label="Agents">
          {agents.map((a) => (
            <AgentRow key={a.user_id} agent={a} />
          ))}
        </ul>
      )}

      {connectOpen && (
        <ConnectAgentModal onClose={() => setConnectOpen(false)} />
      )}
    </div>
  );
}

function AgentRow({ agent }: { agent: Agent }) {
  const [expanded, setExpanded] = useState(false);
  // Local reflection of a revoke: the backend keeps the identity in the list
  // (credential dead until a rotate re-issues), so the "Revoked" state is
  // tracked here and cleared when a rotate mints a fresh token. Persists across
  // list refetches because the row is keyed by `user_id`.
  const [revoked, setRevoked] = useState(false);
  const detailId = `agent-detail-${agent.user_id.replace(/[^a-z0-9]/gi, '-')}`;
  // The org's Representative is not just another agent (ratified one-per-org spec) —
  // its handle's trailing segment is `rep`. Badge it for what it is.
  const isRep = identitySegment(agent.agent_suffix) === REP_SEGMENT;
  // Lead with the NAME, handle secondary (packet 007 / T-0230) — never the handle as
  // the primary identity, even when the mesh set `display_name` to the handle itself.
  const { name, handle } = agentIdentity(agent);

  return (
    <li className={`agents-row${revoked ? ' agents-row-revoked' : ''}`}>
      <button
        type="button"
        className="agents-row-toggle"
        aria-expanded={expanded}
        aria-controls={detailId}
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="agents-row-main">
          <div className="agents-row-name">
            {/* Display name first (packet 007 / T-0230); the handle is the muted
                secondary line below, never the primary identity. */}
            {name}
            {isRep && (
              <span className="agents-row-rep-badge">Representative</span>
            )}
            {revoked && (
              <span className="agents-row-revoked-badge">Revoked</span>
            )}
          </div>
          <div className="agents-row-meta">
            <span className="agents-row-handle">{handle}</span>
            <span className="agents-row-meta-sep" aria-hidden="true">
              ·
            </span>
            <span>Created {formatDate(agent.created_at)}</span>
            {agent.expires_at && (
              <>
                <span className="agents-row-meta-sep" aria-hidden="true">
                  ·
                </span>
                <span>Expires {formatDate(agent.expires_at)}</span>
              </>
            )}
            {agent.paused && (
              <>
                <span className="agents-row-meta-sep" aria-hidden="true">
                  ·
                </span>
                <span>Paused</span>
              </>
            )}
          </div>
        </div>
        <span className="agents-row-toggle-chevron" aria-hidden="true">
          ▾
        </span>
      </button>

      {expanded && (
        <>
          <AgentDetailPanel id={detailId} agentSuffix={agent.agent_suffix} />
          <AgentActions
            agent={agent}
            revoked={revoked}
            onRevoked={() => setRevoked(true)}
            onRotated={() => setRevoked(false)}
          />
        </>
      )}
    </li>
  );
}

// Per-agent credential controls (T-0094 Part B). Rotate mints a fresh token and
// surfaces the new connect command; Revoke goes through a destructive confirm
// modal (modelled on the sign-out ceremony) and kills the credential now.
function AgentActions({
  agent,
  revoked,
  onRevoked,
  onRotated,
}: {
  agent: Agent;
  revoked: boolean;
  onRevoked: () => void;
  onRotated: () => void;
}) {
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [rotateResult, setRotateResult] = useState<RotateAgentResponse | null>(
    null,
  );

  const revokeMutation = useMutation({
    mutationFn: () => revokeAgent(agent.agent_suffix),
    onSuccess: () => {
      onRevoked();
      setConfirmingRevoke(false);
    },
  });

  const rotateMutation = useMutation({
    mutationFn: () => rotateAgent(agent.agent_suffix),
    onSuccess: (resp) => {
      setRotateResult(resp);
      // A fresh token means the agent can connect again — clear the revoked flag.
      onRotated();
    },
  });

  const rotateErr = rotateMutation.error as ApiError | null;
  const agentName = agent.display_name || agent.agent_suffix;

  return (
    <div className="agents-row-actions-bar">
      <div className="settings-actions">
        <button
          type="button"
          className="settings-action-secondary"
          onClick={() => rotateMutation.mutate()}
          disabled={rotateMutation.isPending}
        >
          {rotateMutation.isPending ? 'Rotating…' : 'Rotate token'}
        </button>
        {/* Once revoked, the "Revoked" badge on the row carries the state; the
            recovery path is Rotate (re-issues a token), so the Revoke action is
            retired rather than shown disabled. */}
        {!revoked && (
          <button
            type="button"
            className="settings-action-danger"
            onClick={() => setConfirmingRevoke(true)}
            disabled={revokeMutation.isPending}
          >
            Revoke
          </button>
        )}
      </div>

      {rotateErr && (
        <p className="settings-error" role="alert">
          {rotateErr.status === 429
            ? 'Too many attempts — wait a moment and try again.'
            : rotateErr.status === 404
              ? 'That agent no longer exists.'
              : 'Could not rotate the token. Try again.'}
        </p>
      )}

      {confirmingRevoke && (
        <RevokeConfirmModal
          agentName={agentName}
          isPending={revokeMutation.isPending}
          error={revokeMutation.error as ApiError | null}
          onConfirm={() => revokeMutation.mutate()}
          onCancel={() => {
            revokeMutation.reset();
            setConfirmingRevoke(false);
          }}
        />
      )}

      {rotateResult && (
        <RotateResultModal
          agentName={agentName}
          connectCommand={rotateResult.connect_command}
          onClose={() => setRotateResult(null)}
        />
      )}
    </div>
  );
}

// Destructive confirm for Revoke — modelled on the sign-out ceremony: a clear,
// non-reversible statement of consequence + an explicit destructive button.
function RevokeConfirmModal({
  agentName,
  isPending,
  error,
  onConfirm,
  onCancel,
}: {
  agentName: string;
  isPending: boolean;
  error: ApiError | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const modalRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(modalRef, true);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isPending) onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, isPending]);

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onCancel();
      }}
    >
      <div
        ref={modalRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Revoke ${agentName}`}
      >
        <header className="modal-header">
          <h2>Revoke {agentName}?</h2>
          <button
            type="button"
            className="modal-close"
            aria-label="Close"
            onClick={onCancel}
            disabled={isPending}
          >
            ×
          </button>
        </header>
        <div className="invite-result">
          <p>
            Its connection stops working <strong>immediately</strong> — any live
            sessions are disconnected. You can issue a new token later with
            Rotate.
          </p>
          {error && (
            <p className="settings-error" role="alert">
              {error.status === 429
                ? 'Too many attempts — wait a moment and try again.'
                : error.status === 404
                  ? 'That agent no longer exists.'
                  : 'Could not revoke. Try again.'}
            </p>
          )}
          <div className="invite-actions">
            <button
              type="button"
              className="invite-cancel"
              onClick={onCancel}
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="settings-action-danger"
              onClick={onConfirm}
              disabled={isPending}
            >
              {isPending ? 'Revoking…' : 'Revoke agent'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Rotate result — surfaces the NEW connect command with the same one-click COPY
// affordance as the create-agent flow. The raw token is never rendered; it lives
// only on the clipboard (revealed as selectable text only if the copy fails).
function RotateResultModal({
  agentName,
  connectCommand,
  onClose,
}: {
  agentName: string;
  connectCommand: string;
  onClose: () => void;
}) {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>(
    'idle',
  );
  useFocusTrap(modalRef, true);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (copyState !== 'copied') return;
    const timer = window.setTimeout(() => setCopyState('idle'), 2000);
    return () => window.clearTimeout(timer);
  }, [copyState]);

  async function handleCopy() {
    setCopyState((await copyToClipboard(connectCommand)) ? 'copied' : 'failed');
  }

  const copied = copyState === 'copied';

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={modalRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Token rotated for ${agentName}`}
      >
        <header className="modal-header">
          <h2>Token rotated</h2>
          <button
            type="button"
            className="modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="invite-result">
          <p>
            <strong>{agentName}</strong>&apos;s old token is now dead. Copy the
            new connect command and paste it into the agent&apos;s terminal to
            reconnect.
          </p>
          <button
            type="button"
            className="invite-submit"
            onClick={() => void handleCopy()}
            aria-label="Copy the new connect command to clipboard"
            style={{ alignSelf: 'flex-start' }}
          >
            {copied
              ? '✓ Copied connect command'
              : '📋 Copy the new connect command'}
          </button>
          <p className="invite-expires">
            The command carries a one-time secret — treat it like a password and
            don&apos;t paste it in public channels. It won&apos;t be shown again.
          </p>
          <p
            className="invite-expires"
            role="status"
            aria-live="polite"
            style={{ minHeight: '1rem' }}
          >
            {copied ? 'Copied to clipboard.' : ''}
          </p>
          {copyState === 'failed' && (
            <div className="invite-field" role="alert">
              <span>
                Copy failed — select the command below and copy it manually.
              </span>
              <input
                type="text"
                value={connectCommand}
                readOnly
                aria-label="New connect command"
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
          )}
          <div className="invite-actions">
            <button type="button" className="invite-submit" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentDetailPanel({
  id,
  agentSuffix,
}: {
  id: string;
  agentSuffix: string;
}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['agent', agentSuffix],
    queryFn: () => fetchAgent(agentSuffix),
  });

  if (isLoading) {
    return (
      <div
        id={id}
        className="agents-row-detail"
        role="status"
        aria-label="Loading agent details"
      >
        <span className="agents-row-detail-skeleton">Loading…</span>
      </div>
    );
  }

  if (isError || !data) {
    const apiError = error as unknown as ApiError | undefined;
    return (
      <div id={id} className="agents-row-detail" role="alert">
        {apiError?.status === 401
          ? 'Sign in to view agent details.'
          : 'Could not load agent details.'}
      </div>
    );
  }

  return <AgentDetailFields id={id} detail={data} />;
}

function AgentDetailFields({
  id,
  detail,
}: {
  id: string;
  detail: AgentDetail;
}) {
  return (
    <div id={id} className="agents-row-detail">
      <div className="agents-row-detail-field">
        <span className="agents-row-detail-label">User ID</span>
        <code className="agents-row-detail-value settings-cli-hint-code">
          {detail.user_id}
        </code>
      </div>
      <div className="agents-row-detail-field">
        <span className="agents-row-detail-label">Kind</span>
        <span className="agents-row-detail-value">{detail.kind}</span>
      </div>
      {detail.room_id && (
        <div className="agents-row-detail-field">
          <span className="agents-row-detail-label">Space</span>
          <span className="agents-row-detail-value">{detail.room_id}</span>
        </div>
      )}
      <div className="agents-row-detail-field">
        <span className="agents-row-detail-label">Last active</span>
        <span className="agents-row-detail-value">
          {detail.last_activity_at ? formatDate(detail.last_activity_at) : '—'}
        </span>
      </div>
      <div className="agents-row-detail-field">
        <span className="agents-row-detail-label">Created</span>
        <span className="agents-row-detail-value">
          {formatDate(detail.created_at)}
        </span>
      </div>
    </div>
  );
}
